import { useEffect, useState } from "react";
import { useGetIdentity, useNotify, useRedirect } from "ra-core";
import {
  PackageOpen, Plus, Search, Edit2, ExternalLink, X,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { getSupabaseClient } from "../providers/supabase/supabase";

type RMA = {
  id: number;
  rma_number: string;
  order_id: number | null;
  order_number?: string;
  contact_name?: string;
  type: string;
  reason: string;
  quantity: number | null;
  amount: number;
  currency: string;
  status: string;
  resolution: string | null;
  description: string | null;
  created_at: string;
};

const STATUS_BADGE: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-700",
  approved: "bg-blue-100 text-blue-700",
  rejected: "bg-red-100 text-red-700",
  in_process: "bg-purple-100 text-purple-700",
  closed: "bg-green-100 text-green-700",
};

const TYPE_LABELS: Record<string, string> = {
  return: "退货", exchange: "换货", refund: "退款",
};

const REASON_LABELS: Record<string, string> = {
  quality: "质量问题", shortage: "数量短缺", wrong_item: "发错货品",
  packaging: "包装损坏", delay: "延误", other: "其他",
};

const STATUS_LABELS: Record<string, string> = {
  pending: "待处理", approved: "已批准", rejected: "已拒绝",
  in_process: "处理中", closed: "已关闭",
};

const RESOLUTION_LABELS: Record<string, string> = {
  reship: "补发", refund: "退款", credit: "赊账抵扣", no_action: "不处理",
};

type FormState = {
  order_id: string;
  type: string;
  reason: string;
  quantity: string;
  amount: string;
  currency: string;
  status: string;
  resolution: string;
  description: string;
};

const emptyForm = (): FormState => ({
  order_id: "", type: "return", reason: "quality",
  quantity: "", amount: "", currency: "USD",
  status: "pending", resolution: "", description: "",
});

const RMAPage = () => {
  const { identity } = useGetIdentity();
  const notify = useNotify();
  const redirect = useRedirect();
  const [rmas, setRmas] = useState<RMA[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<RMA | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await getSupabaseClient()
      .from("rma_records")
      .select(`
        id, rma_number, order_id, type, reason, quantity, amount,
        currency, status, resolution, description, created_at,
        orders ( order_number, contacts ( first_name, last_name ) )
      `)
      .order("created_at", { ascending: false });

    setRmas((data ?? []).map((r: any) => ({
      id: r.id,
      rma_number: r.rma_number ?? `RMA-${r.id}`,
      order_id: r.order_id,
      order_number: r.orders?.order_number ?? (r.order_id ? `ORD-${String(r.order_id).padStart(4,"0")}` : null),
      contact_name: r.orders?.contacts
        ? `${r.orders.contacts.first_name} ${r.orders.contacts.last_name}`
        : null,
      type: r.type,
      reason: r.reason,
      quantity: r.quantity,
      amount: r.amount ?? 0,
      currency: r.currency,
      status: r.status,
      resolution: r.resolution,
      description: r.description,
      created_at: r.created_at,
    })));
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const set = (k: keyof FormState, v: string) => setForm(f => ({ ...f, [k]: v }));

  const openCreate = () => { setEditing(null); setForm(emptyForm()); setShowForm(true); };
  const openEdit = (r: RMA) => {
    setEditing(r);
    setForm({
      order_id: String(r.order_id ?? ""),
      type: r.type, reason: r.reason,
      quantity: r.quantity != null ? String(r.quantity) : "",
      amount: String(r.amount),
      currency: r.currency, status: r.status,
      resolution: r.resolution ?? "",
      description: r.description ?? "",
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    setSaving(true);
    const supabase = getSupabaseClient();
    const payload = {
      order_id: form.order_id ? parseInt(form.order_id) : null,
      type: form.type,
      reason: form.reason,
      quantity: form.quantity ? parseInt(form.quantity) : null,
      amount: parseFloat(form.amount) || 0,
      currency: form.currency,
      status: form.status,
      resolution: form.resolution || null,
      description: form.description || null,
      updated_at: new Date().toISOString(),
    };
    let error;
    if (editing) {
      ({ error } = await supabase.from("rma_records").update(payload).eq("id", editing.id));
    } else {
      ({ error } = await supabase.from("rma_records").insert({
        ...payload, created_by: identity?.id ?? null,
      }));
    }
    if (error) { notify(error.message, { type: "error" }); }
    else { notify(editing ? "已更新" : "已创建", { type: "success" }); setShowForm(false); load(); }
    setSaving(false);
  };

  const filtered = rmas.filter(r => {
    if (statusFilter !== "all" && r.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return r.rma_number.toLowerCase().includes(q)
        || (r.order_number ?? "").toLowerCase().includes(q)
        || (r.contact_name ?? "").toLowerCase().includes(q);
    }
    return true;
  });

  // Summary counts
  const openCount = rmas.filter(r => ["pending","approved","in_process"].includes(r.status)).length;
  const totalRefund = rmas.filter(r => r.status !== "rejected").reduce((s, r) => s + r.amount, 0);

  return (
    <div className="p-6 space-y-5 max-w-5xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <PackageOpen className="w-6 h-6" /> 退货换货 (RMA)
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            处理中 {openCount} 件 · 涉及金额 ${Math.round(totalRefund).toLocaleString()}
          </p>
        </div>
        <Button size="sm" onClick={openCreate} className="gap-1">
          <Plus className="w-3.5 h-3.5" /> 新建 RMA
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 items-center">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-muted-foreground" />
          <Input className="pl-8 h-8 text-sm" placeholder="RMA号、订单号、客户..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="flex border rounded-lg overflow-hidden">
          {["all", "pending", "approved", "in_process", "closed"].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 text-xs font-medium transition-colors capitalize ${statusFilter === s ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              {s === "all" ? "全部" : STATUS_LABELS[s]}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-14 bg-muted animate-pulse rounded-lg" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <PackageOpen className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p>暂无退货记录</p>
        </div>
      ) : (
        <div className="bg-card border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 text-xs text-muted-foreground uppercase">
                <th className="text-left px-4 py-2.5">RMA 编号</th>
                <th className="text-left px-4 py-2.5">关联订单</th>
                <th className="text-center px-4 py-2.5">类型 / 原因</th>
                <th className="text-right px-4 py-2.5">金额</th>
                <th className="text-center px-4 py-2.5">处理方式</th>
                <th className="text-center px-4 py-2.5">状态</th>
                <th className="px-4 py-2.5 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.id} className="border-t hover:bg-muted/20 transition-colors">
                  <td className="px-4 py-3">
                    <div className="font-mono text-xs text-muted-foreground">{r.rma_number}</div>
                    <div className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</div>
                  </td>
                  <td className="px-4 py-3">
                    {r.order_id ? (
                      <button onClick={() => redirect("show", "orders", r.order_id!)}
                        className="text-primary hover:underline text-xs flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" /> {r.order_number}
                      </button>
                    ) : <span className="text-muted-foreground text-xs">—</span>}
                    {r.contact_name && <div className="text-xs text-muted-foreground">{r.contact_name}</div>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="text-xs font-medium">{TYPE_LABELS[r.type] ?? r.type}</div>
                    <div className="text-xs text-muted-foreground">{REASON_LABELS[r.reason] ?? r.reason}</div>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums font-medium">
                    {r.amount > 0 ? `${r.currency === "USD" ? "$" : "€"}${r.amount.toFixed(2)}` : "—"}
                  </td>
                  <td className="px-4 py-3 text-center text-xs">
                    {r.resolution ? RESOLUTION_LABELS[r.resolution] ?? r.resolution : "—"}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_BADGE[r.status] ?? "bg-muted"}`}>
                      {STATUS_LABELS[r.status] ?? r.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => openEdit(r)} className="p-1 text-muted-foreground hover:text-foreground">
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={o => !o && setShowForm(false)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "编辑 RMA" : "新建 RMA"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">关联订单 ID</Label>
                <Input className="h-8 text-sm" type="number" placeholder="订单 ID" value={form.order_id} onChange={e => set("order_id", e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">货币</Label>
                <Select value={form.currency} onValueChange={v => set("currency", v)}>
                  <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">类型 *</Label>
                <Select value={form.type} onValueChange={v => set("type", v)}>
                  <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TYPE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">原因 *</Label>
                <Select value={form.reason} onValueChange={v => set("reason", v)}>
                  <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(REASON_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">数量</Label>
                <Input className="h-8 text-sm" type="number" min={0} value={form.quantity} onChange={e => set("quantity", e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">索赔金额</Label>
                <Input className="h-8 text-sm" type="number" step="0.01" min={0} value={form.amount} onChange={e => set("amount", e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">状态</Label>
                <Select value={form.status} onValueChange={v => set("status", v)}>
                  <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">处理方式</Label>
                <Select value={form.resolution} onValueChange={v => set("resolution", v)}>
                  <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">未确定</SelectItem>
                    {Object.entries(RESOLUTION_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">问题描述</Label>
              <Textarea className="text-sm resize-none" rows={3} value={form.description} onChange={e => set("description", e.target.value)} />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => setShowForm(false)}>取消</Button>
            <Button size="sm" onClick={handleSave} disabled={saving}>{saving ? "保存中..." : (editing ? "更新" : "创建")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RMAPage;
